import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CTable from '../../components/shared-ui/CTable';
import {
  Button,
  Drawer,
  Popconfirm,
  Form,
  Space,
  TableProps,
  notification,
} from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useConnection, useUser } from '@sezenta/connection';
import { useBusinessContext } from '../../context/BusinessContext';
import { DataEditModal, useDataEditModal } from '../../common/DataEditModal';
import { FormSchema } from '@sezenta/antd-schema-form';

import { useEventListener, useEvents } from '../../common/EventContext';
import SiteViewModal, { useSiteViewModal } from './SiteViewModal';
import SiteScheduleModal, { useSiteScheduleModal } from './SiteScheduleModal';
import { QRCodeSVG } from 'qrcode.react';
import { Permission, User } from '../../dto';
import { createRoot } from 'react-dom/client';
import { useNavigate } from 'react-router-dom';
import CTag from '../../components/ui/CTag';
import {
  FilterFields,
  FilterPanel,
  FilterValues,
} from '../../common/antd-filter-panel';

const Sites = () => {
  interface Site {
    id: string;
    name: string;
    address: string;
    status: string;
    area: number;
    manager: string;
    pCode: string;
    phone: string;
    state: number;
    suburb: number;
    zone: number;
    latitude: number;
    longitude: number;
    council: number;
    email: string;
    costCenter?: string;
    storeEmail?: string;
    storeOpeningDate?: string;
    storeClosingDate?: string;
    reopeningDate?: string;
  }

  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const connection = useConnection();
  const events = useEvents();
  const [site, setSite] = useState<any>();
  const [form] = Form.useForm();
  const editModal = useDataEditModal();
  const viewModal = useSiteViewModal();
  const scheduleModal = useSiteScheduleModal();
  const [loadingDelete, setLoadingDelete] = useState<boolean>(false);
  const [qrCodeModalVisible, setQrCodeModalVisible] = useState(false);
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const { states, zones, suburbs, councils, businessId, select } =
    useBusinessContext();
  const [value, setValue] = useState<FilterValues>([]);
  const user = useUser();
  const navigate = useNavigate();
  useEffect(() => {
    setLoading(true);
    connection
      .get(`sites/${businessId}`)
      .then((res) => {
        setSites(res?.data ?? []);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [businessId, connection]);

  useEventListener(
    'SITE',
    useCallback((evt) => {
      if (evt.action === 'CREATE') {
        setSites((prevSites) => [evt.data, ...prevSites]);
      } else if (evt.action === 'UPDATE') {
        setSites((prevSites) =>
          prevSites.map((s) => (s.id === evt.data.id ? evt.data : s)),
        );
      } else {
        setSites((prevSites) => prevSites.filter((s) => s.id !== evt.data.id));
      }
    }, []),
  );

  const showQrCodeModal = (site: Site) => {
    setSelectedSite(site);
    setQrCodeModalVisible(true);
  };

  const printQRCode = () => {
    if (!selectedSite) return;

    // Define the base URL
    const baseUrl = 'https://xtns9zalof.execute-api.us-east-1.amazonaws.com'; // Replace with your actual base URL
    const qrCodeUrl = `${baseUrl}/${selectedSite.name}/${selectedSite.id}`;

    // Open a new print window
    const printWindow = window.open('', '', 'width=600,height=400');
    if (!printWindow) return;

    // Create a temporary container for rendering QR code
    const qrCodeContainer = document.createElement('div');
    document.body.appendChild(qrCodeContainer);

    // Generate the QR code inside the container
    const root = createRoot(qrCodeContainer);
    root.render(
      <div>
        <QRCodeSVG value={qrCodeUrl} size={400} />
        <div style={{ marginTop: 20 }}>
          <h3>{selectedSite.name}</h3>
          <p>{selectedSite.address}</p>
        </div>
      </div>,
    );

    // Wait for the QR code to render before printing
    setTimeout(() => {
      const qrCodeHTML = qrCodeContainer.innerHTML;
      document.body.removeChild(qrCodeContainer); // Clean up

      // Write the QR code to the print window
      printWindow.document.write(`
      <html>
      <head><title>Print QR Code</title></head>
      <body style="text-align: center;">
        <h3>QR Code for Site:  <p><a href="${qrCodeUrl}" target="_blank">${qrCodeUrl}</a></p></h3>
        ${qrCodeHTML}
        <script>
          window.onload = function() {
            window.print();
            setTimeout(() => window.close(), 100);
          };
        </script>
      </body>
      </html>
    `);
      printWindow.document.close();
    }, 500); // Give it time to render before printing
  };

  const handleDelete = useCallback(
    async (record: any) => {
      try {
        setLoadingDelete(true);
        await connection.delete(`sites/${businessId}/${record.id}`);
        setSites((prevState) => prevState.filter((s) => s.id !== record.id));
        setLoadingDelete(false);
      } catch (e) {
        console.log(e);
        setLoadingDelete(false);
      }
    },
    [connection, businessId],
  );

  const columns: TableProps<any>['columns'] = [
    {
      title: 'Store ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Store Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: User) => (
        <Space size="middle">
          <span
            className="text-teal-500 flex gap-2 items-center cursor-pointer hover:text-blue-500"
            onClick={() => viewModal.current?.view(record)}
          >
            {text}
          </span>
        </Space>
      ),
    },
    {
      title: 'Geo Position',
      dataIndex: '',
      key: 'latitudeLongitude',
      render: (_, record) =>
        `${record.latitude || ''}${record.latitude && record.longitude ? ', ' : ''}${record.longitude || ''}`,
    },
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
      render: (state) => states[state]?.name,
    },
    {
      title: 'Zone',
      dataIndex: 'zone',
      key: 'zone',
      render: (zone) => zones[zone]?.name,
    },

    {
      title: 'Council',
      dataIndex: 'council',
      key: 'council',
      render: (council) => councils[council]?.name,
    },
    {
      title: 'Suburb',
      dataIndex: 'suburb',
      key: 'suburb',
      render: (suburb) => suburbs[suburb]?.name,
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
    },
    {
      title: 'Cost Center',
      dataIndex: 'costCenter',
      key: 'costCenter',
    },
    {
      title: 'Status',
      key: 'status',
      dataIndex: 'status',
      render: (status: string) => {
        const isActive = status === 'Active';
        return (
          <CTag prefix="dot" color={isActive ? 'green' : 'red'}>
            {isActive ? 'ACTIVE' : 'INACTIVE'}
          </CTag>
        );
      },
    },
    {
      title: 'Schedules',
      key: 'schedules',
      fixed: 'right',
      hidden: !user?.permissions.includes(Permission.VIEW_SITES_SCHEDULE),
      render: (_, record) => (
        <Button
          title="Manage Collection Schedules"
          onClick={() => scheduleModal.current?.open(record.id, record.name)}
          type="text"
          className="!text-[16px] !bg-[#E6F7FF] !text-[#1890FF]"
        >
          📅 Schedules
        </Button>
      ),
    },
    {
      title: 'QR Code',
      key: 'qrCode',
      fixed: 'right',
      render: (_, record) => (
        <Button
          title="View QR Code"
          onClick={() => showQrCodeModal(record)}
          type="text"
          className="!text-[16px] !bg-[#DCF0F1] !text-[#667085]"
        >
          View QR Code
        </Button>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right',
      render: (_, record) => (
        <Space size="middle">
          {user?.permissions.includes(Permission.EDIT_SITE) && (
            <Button
              title="Edit Store"
              icon={<EditOutlined />}
              type="text"
              className="text-[20px] text-gray-500"
              // onClick={() => handleEdit(record)}
              onClick={() => {
                // The schema-form `date` adapter decodes any non-undefined value
                // with dayjs(). A stored `null`/'' (store created without these
                // dates) becomes an Invalid Date, which renders the picker as all
                // invalid. Drop empty date fields so decode is skipped.
                const clean = (v?: string) => (v ? v : undefined);
                editModal.current?.edit(
                  {
                    ...record,
                    storeOpeningDate: clean(record.storeOpeningDate),
                    storeClosingDate: clean(record.storeClosingDate),
                    reopeningDate: clean(record.reopeningDate),
                  },
                  siteEditSchema,
                  'Edit Store',
                  `sites/${businessId}/${record.id}`,
                );
              }}
            />
          )}
          <Popconfirm
            okButtonProps={{ loading: loadingDelete }}
            onConfirm={() => handleDelete(record)}
            placement={'topLeft'}
            title={`Are you sure to delete this store?`}
          >
            {user?.permissions.includes(Permission.DELETE_SITE) && (
              <Button
                icon={<DeleteOutlined />}
                type="text"
                className="!text-[20px] !text-gray-500"
              />
            )}
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // When a geographic master-data list isn't configured for this project, the
  // select would just render a bare "No data". Instead, explain why it's empty and
  // (for users who can) link straight to the right Master Data page. The link opens
  // in a new tab so the half-filled store form isn't lost.
  const canConfigureMasterData =
    user?.permissions.includes(Permission.CREATE_MASTER_DATA) ||
    user?.permissions.includes(Permission.EDIT_MASTER_DATA);

  const mdEmptyHint = useCallback(
    (label: string, type: string, options: any[]) =>
      options.length === 0 ? (
        <div className="py-3 px-2 text-center">
          <div className="text-[13px] text-[#5E6870] mb-1">
            No {label} configured for this project yet.
          </div>
          {canConfigureMasterData ? (
            <a
              onClick={() =>
                navigate(`/portal/${businessId}/master-data/${type}`)
              }
              className="text-[12px] font-medium text-[#26A3A7] cursor-pointer"
            >
              Configure {label} →
            </a>
          ) : (
            <div className="text-[12px] text-[#84919A]">
              Ask an admin to add them in Master Data.
            </div>
          )}
        </div>
      ) : undefined,
    [businessId, canConfigureMasterData, navigate],
  );

  const siteSchema: FormSchema = useMemo(
    () => [
      {
        id: 'name',
        name: 'Store Name',
        type: 'string',
        props: { placeholder: 'Store Name' },
        options: {
          rules: [{ required: true, message: 'Store Name is required.' }],
        },
      },
      {
        id: 'state',
        name: 'State',
        type: 'select',
        props: {
          showSearch: true,
          placeholder: 'Select State',
          options: select?.states ?? [],
          optionFilterProp: 'label',
          notFoundContent: mdEmptyHint('states', 'State', select?.states ?? []),
        },
        options: {
          rules: [{ required: true, message: 'State is required.' }],
        },
      },
      {
        id: 'council',
        name: 'Council',
        type: 'select',
        props: {
          showSearch: true,
          placeholder: 'Council',
          options: select?.councils ?? [],
          optionFilterProp: 'label',
          notFoundContent: mdEmptyHint(
            'councils',
            'Council',
            select?.councils ?? [],
          ),
        },
        options: {
          rules: [{ required: true, message: 'Council is required.' }],
        },
      },
      {
        id: 'zone',
        name: 'Zone',
        type: 'select',
        props: {
          showSearch: true,
          placeholder: 'Zone',
          options: select?.zones ?? [],
          optionFilterProp: 'label',
          notFoundContent: mdEmptyHint('zones', 'Zone', select?.zones ?? []),
        },
        options: {
          rules: [{ required: true, message: 'Zone is required.' }],
        },
      },
      {
        id: 'area',
        name: 'Area',
        type: 'select',
        props: {
          showSearch: true,
          placeholder: 'Select Area',
          options: select?.areas ?? [],
          optionFilterProp: 'label',
          notFoundContent: mdEmptyHint('areas', 'Area', select?.areas ?? []),
        },
        options: {
          rules: [{ required: true, message: 'Area is required.' }],
        },
      },
      {
        id: 'suburb',
        name: 'Suburb',
        type: 'select',
        props: {
          showSearch: true,
          placeholder: 'Select Suburb',
          options: select?.suburbs ?? [],
          optionFilterProp: 'label',
          notFoundContent: mdEmptyHint(
            'suburbs',
            'Suburb',
            select?.suburbs ?? [],
          ),
        },
        options: {
          rules: [{ required: true, message: 'Suburb is required.' }],
        },
      },
      {
        id: 'address',
        name: 'Address',
        type: 'string',
        props: { placeholder: 'Address' },
        options: {
          rules: [{ required: true, message: 'Address is required.' }],
        },
      },
      {
        id: 'pCode',
        name: 'Postal Code',
        type: 'string',
        props: { placeholder: 'Postal Code' },
        options: {
          rules: [{ required: true, message: 'Postal Code is required.' }],
        },
      },
      {
        id: 'status',
        name: 'Status',
        type: 'select',
        props: {
          placeholder: 'Select status',
          options: ['Active', 'Inactive'],
        },
        options: {
          rules: [{ required: true, message: 'Status is required.' }],
        },
      },
      {
        id: 'storeEmail',
        name: 'Store Contact',
        type: 'string',
        props: { placeholder: 'Store Contact' },
        options: {
          rules: [{ required: false, message: 'Store contact is required.' }],
        },
      },
      {
        id: 'phone',
        name: 'Phone',
        type: 'string',
        props: { placeholder: 'Phone' },
        options: {
          rules: [{ required: false, message: 'Phone is required.' }],
        },
      },
      {
        id: 'latitude',
        name: 'Latitude',
        type: 'string',
        layout: { md: 12 },
        props: {
          placeholder: 'Latitude',
          type: 'number',
        },
        options: {
          rules: [{ required: true, message: 'Latitude is required.' }],
        },
      },
      {
        id: 'longitude',
        name: 'Longitude',
        layout: { md: 12 },
        type: 'string',
        props: {
          placeholder: 'Longitude',
          type: 'number',
        },
        options: {
          rules: [{ required: true, message: 'Longitude is required.' }],
        },
      },
      {
        id: 'storeOpeningDate',
        name: 'Store Opening Date',
        type: 'date',
        props: { placeholder: 'Store Opening Date' },
        layout: { md: 12 },
      },
      {
        id: 'storeClosingDate',
        name: 'Store Closing Date',
        type: 'date',
        props: { placeholder: 'Store Closing Date' },
        layout: { md: 12 },
      },
      {
        id: 'reopeningDate',
        name: 'Reopening Date',
        type: 'date',
        props: { placeholder: 'Reopening Date' },
        layout: { md: 12 },
      },
      {
        id: 'costCenter',
        name: 'Cost center',
        type: 'string',
        props: { placeholder: 'Cost center', maxLength: 50 },
        layout: { md: 12 },
      },
    ],
    [select, mdEmptyHint],
  );

  const siteEditSchema = useMemo(
    () => [
      ...siteSchema,
      // {
      //   id: 'status',
      //   name: 'Status',
      //   type: 'select',
      //   props: {
      //     placeholder: 'Select status',
      //     options: ['Active', 'Inactive'],
      //   },
      //   options: {
      //     rules: [{ required: true, message: 'Status is required.' }],
      //   },
      // },
    ],
    [siteSchema],
  );
  const filterSchema: FilterFields = useMemo(
    () => [
      {
        label: 'Status',
        name: 'status',
        operators: ['eq'],
        type: 'radio',
        options: [
          { value: 'Active', label: 'Active' },
          { value: 'Inactive', label: 'Inactive' },
        ],
      },
      {
        label: 'Suburb',
        name: 'suburb',
        operators: ['eq'],
        type: 'select',
        options: select['suburbs'],
        masterDataType: 'suburbs',
      },
      {
        label: 'Zone',
        name: 'zone',
        operators: ['eq'],
        type: 'select',
        options: select['zones'],
        masterDataType: 'zone',
      },
      {
        label: 'State',
        name: 'state',
        operators: ['eq'],
        type: 'select',
        options: select['states'],
        masterDataType: 'states',
      },
    ],
    [select],
  );

  const filteredSites = useMemo(() => {
    if (!value || value.length === 0) {
      return sites;
    }

    return sites.filter((site) => {
      // Check if the site matches all filter conditions
      return value.every((condition) => {
        const { name, operator, value } = condition;

        // Handle different field types and operators
        if (operator === 'eq') {
          // Type-safe property access using a type assertion
          return (site as Record<string, any>)[name] === value;
        }

        return true;
      });
    });
  }, [sites, value]);
  const items = [
    { title: 'Dashboard', href: 'dashboard' },
    { title: 'Admin', href: '#' },
    { title: 'Sites', href: 'sites' },
  ];

  return (
    <div className="w-full h-full p-5 ctable">
      {/*<div className="w-full pl-3 flex justify-start mb-3">*/}
      {/*  <Breadcrumb separator=">" items={items} />*/}
      {/*</div>*/}
      <div className="flex pl-3 pb-4">
        <div className="flex gap-3 items-center mr-auto">
          <div className="text-[24px] font-[600]">Stores</div>
        </div>
      </div>
      <CTable
        loading={loading}
        title="Stores"
        columns={columns}
        searchFields={['name', 'address']}
        hideAddButton={!user?.permissions.includes(Permission.CREATE_SITE)}
        tableProps={{
          dataSource: filteredSites,
          onRow: (record) => ({
            onClick: () => {
              if (user?.permissions.includes(Permission.VIEW_SITES)) {
                viewModal.current?.view(record);
              } else {
                notification.error({
                  message: 'Access Denied',
                  description: "You don't have permission to view this site.",
                });
              }
            },
          }),
        }}
        data={filteredSites}
        addBtnTitle="Add Store"
        onAdd={() =>
          // user?.permissions.includes(Permission.CREATE_SITE) &&
          editModal.current?.create(
            siteSchema,
            'Create Store',
            `sites`,
            (data) => {
              data.businessId = businessId;
              return data;
            },
          )
        }
        filterComponent={
          <FilterPanel
            fields={filterSchema}
            value={value}
            onChange={(c) => {
              setValue(c ?? []);
              console.log(JSON.stringify(c, null, 2));
            }}
          />
        }
      />
      <SiteViewModal ref={viewModal} />
      <SiteScheduleModal ref={scheduleModal} />
      <DataEditModal type={'SITE'} ref={editModal} />
      <Drawer
        title="QR Code"
        placement="right"
        onClose={() => setQrCodeModalVisible(false)}
        open={qrCodeModalVisible}
        width={500}
      >
        {selectedSite && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            {/*<QRCodeSVG value={`${selectedSite.id}`} size={370} />*/}
            <QRCodeSVG
              value={`${window.location.origin}/${selectedSite.name}/${selectedSite.id}`}
              size={370}
            />
            <div className={'mt-10'}>
              <h3>{selectedSite.name}</h3>
              <p>{selectedSite.address}</p>
            </div>
          </div>
        )}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '10px',
            marginRight: '5px',
          }}
        >
          <Button
            onClick={() => setQrCodeModalVisible(false)}
            style={{ marginLeft: '280px' }}
          >
            Cancel
          </Button>
          <Button type="primary" onClick={printQRCode}>
            Print
          </Button>
        </div>
      </Drawer>
    </div>
  );
};

export default Sites;
